"use client";

import { Card, CardBody, CardFooter } from "@heroui/card";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import type { LanguageCode } from "@/config/i18n";
import type { Movie, SortOption } from "@/types/movie";
import { CardContainer, CardItem } from "@/components/ui/card-3d";
import { LibraryStatusBadges } from "@/components/library/LibraryStatusBadges";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { Tooltip } from "@/components/ui/Tooltip";

import { MoviePoster } from "./MoviePoster";

interface MovieCardProps {
  movie: Movie;
  sortBy?: SortOption;
}

// Map language codes to locale codes for date formatting
const localeMap: Record<LanguageCode, string> = {
  en: "en-US",
  ko: "ko-KR",
  ja: "ja-JP",
};

export function MovieCard({ movie, sortBy }: MovieCardProps) {
  const pathname = usePathname();
  const lang = getLanguageFromPathname(pathname);
  const t = createTranslator(lang);
  const currentLocale = localeMap[lang] || "en-US";

  const releaseYear = movie.releaseYear || "N/A";

  // Get sort data to display in top right
  const getSortData = () => {
    if (!sortBy) return null;

    switch (sortBy) {
      case "runtime":
        if (!movie.runtime) return null;
        const hours = Math.floor(movie.runtime / 60);
        const minutes = movie.runtime % 60;

        if (hours > 0 && minutes > 0) {
          return `${hours}h ${minutes}m`;
        } else if (hours > 0) {
          return `${hours}h`;
        } else {
          return `${minutes}m`;
        }
      case "box_office":
        return movie.boxOffice && movie.boxOffice !== "N/A"
          ? movie.boxOffice
          : null;
      case "release_date":
        if (!movie.releaseDate) return null;
        const date = new Date(movie.releaseDate);

        return date.toLocaleDateString(currentLocale, {
          month: "short",
          day: "numeric",
          year: "numeric",
          // Release dates are calendar dates: format them the same way on the
          // server and in every browser time zone.
          timeZone: "UTC",
        });
      case "awards": {
        if (!movie.awards || movie.awards === "N/A") return null;

        const oscarWinsMatch = movie.awards.match(/Won (\d+) Oscars?/i);
        const winsMatch = movie.awards.match(/(\d+)\s+wins?/i);
        const nominationsMatch = movie.awards.match(/(\d+)\s+nominations?/i);

        const parts = [];

        if (oscarWinsMatch) {
          const count = parseInt(oscarWinsMatch[1]);
          const oscarText = t(count > 1 ? "movies.oscars" : "movies.oscar");

          parts.push(`${count} ${oscarText}`);
        }

        if (winsMatch) {
          const count = parseInt(winsMatch[1]);
          const winText = t(count > 1 ? "movies.wins" : "movies.win");

          parts.push(`${count} ${winText}`);
        }

        if (nominationsMatch) {
          const count = parseInt(nominationsMatch[1]);
          const nominationText = t(
            count > 1 ? "movies.nominations" : "movies.nomination",
          );

          parts.push(`${count} ${nominationText}`);
        }

        return parts.length > 0 ? parts.join(", ") : null;
      }
      default:
        return null;
    }
  };

  const sortData = getSortData();

  return (
    <CardContainer className="w-full">
      {/* One interactive element per card: the link (the Card is not pressable). */}
      <Link
        className="block w-full rounded-large outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        href={`/${lang}/movies/${movie.slug}`}
      >
        <Card className="group relative overflow-hidden transition-transform active:scale-[0.98] motion-reduce:transition-none">
          <CardBody className="p-0">
            <CardItem className="w-full" translateZ="50">
              <div
                className="relative w-full overflow-hidden bg-gray-200 dark:bg-gray-800"
                style={{ aspectRatio: "2/3" }}
              >
                <MoviePoster
                  alt={movie.title}
                  className="object-cover object-center transition-opacity group-hover:opacity-75"
                  fallback={
                    <div className="flex h-full items-center justify-center text-gray-400">
                      <span className="text-4xl">🎬</span>
                    </div>
                  }
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  src={movie.posterUrl}
                />

                {/* The signed-in user's library status (in watchlist, watched, rating) */}
                <CardItem
                  className="absolute left-2 top-2 z-10"
                  translateZ="100"
                >
                  <LibraryStatusBadges lang={lang} movieId={String(movie.id)} />
                </CardItem>

                {/* Rating badges and sort data */}
                <CardItem
                  className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1"
                  translateZ="100"
                >
                  {/* Sort data badge */}
                  {sortData && (
                    <div className="rounded-md bg-black/70 px-2 py-1 backdrop-blur-sm">
                      <span className="text-xs font-semibold text-white whitespace-nowrap">
                        {sortData}
                      </span>
                    </div>
                  )}
                  {/* IMDB Rating */}
                  {movie.externalRatings?.imdb && (
                    <Tooltip content={t("movies.imdbRating")}>
                      <div className="flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 backdrop-blur-sm">
                        <Image
                          alt="IMDB"
                          className="h-3 w-3"
                          height={12}
                          src="/IMDB-logo.svg"
                          width={12}
                        />
                        <span className="text-xs font-semibold text-white">
                          {parseFloat(movie.externalRatings.imdb).toFixed(1)}
                        </span>
                      </div>
                    </Tooltip>
                  )}

                  {/* Rotten Tomatoes Critic Rating */}
                  {movie.externalRatings?.rottenTomatoesCritic && (
                    <Tooltip content={t("movies.rtCriticRating")}>
                      <div className="flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 backdrop-blur-sm">
                        <Image
                          alt="RT Critic"
                          className="h-3 w-3"
                          height={12}
                          src="/rotten-tomatoes-critic-logo.svg"
                          width={12}
                        />
                        <span className="text-xs font-semibold text-white">
                          {movie.externalRatings.rottenTomatoesCritic.replace(
                            "%",
                            "",
                          )}
                        </span>
                      </div>
                    </Tooltip>
                  )}

                  {/* Rotten Tomatoes Audience Rating */}
                  {movie.externalRatings?.rottenTomatoesAudience && (
                    <Tooltip content={t("movies.rtAudienceRating")}>
                      <div className="flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 backdrop-blur-sm">
                        <Image
                          alt="RT Audience"
                          className="h-3 w-3"
                          height={12}
                          src="/rotten-tomatoes-audience-logo.svg"
                          width={12}
                        />
                        <span className="text-xs font-semibold text-white">
                          {movie.externalRatings.rottenTomatoesAudience.replace(
                            "%",
                            "",
                          )}
                        </span>
                      </div>
                    </Tooltip>
                  )}

                  {/* TMDB Rating */}
                  {movie.rating > 0 && (
                    <Tooltip content={t("movies.tmdbRating")}>
                      <div className="flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 backdrop-blur-sm">
                        <Image
                          alt="TMDB"
                          className="h-3 w-3"
                          height={12}
                          src="/tmdb-logo.svg"
                          width={12}
                        />
                        <span className="text-xs font-semibold text-white">
                          {movie.rating.toFixed(1)}
                        </span>
                      </div>
                    </Tooltip>
                  )}

                  {/* Cage MDB Rating */}
                  {movie.externalRatings?.cageMdb && (
                    <Tooltip content={t("movies.cageMdbRating")}>
                      <div className="flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 backdrop-blur-sm">
                        <Image
                          alt="Cage MDB"
                          className="h-4 w-3"
                          height={16}
                          src="/nicolas-cage.png"
                          width={12}
                        />
                        <span className="text-xs font-semibold text-white">
                          {parseFloat(movie.externalRatings.cageMdb).toFixed(1)}
                        </span>
                      </div>
                    </Tooltip>
                  )}
                </CardItem>
              </div>
            </CardItem>
          </CardBody>

          <CardFooter className="flex-col items-start gap-1 p-3">
            <h3 className="line-clamp-2 w-full text-sm font-semibold">
              {movie.title}
            </h3>
            <div className="flex w-full items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <span>{releaseYear}</span>
              {movie.genres.length > 0 && (
                <span className="line-clamp-1">{movie.genres[0]}</span>
              )}
            </div>
          </CardFooter>
        </Card>
      </Link>
    </CardContainer>
  );
}

/**
 * Skeleton version of MovieCard for loading states
 */
export function MovieCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardBody className="p-0">
        <div className="aspect-[2/3] w-full animate-pulse bg-gray-300 dark:bg-gray-700" />
      </CardBody>
      <CardFooter className="flex-col items-start gap-2 p-3">
        <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-gray-300 dark:bg-gray-700" />
      </CardFooter>
    </Card>
  );
}
